"use client";

import { useEffect, useState } from "react";
import { AvatarCard } from "./components/AvatarCard";
import { Toast } from "./components/Toast";

const OutpaceLogo = () => (
	<svg
		aria-hidden="true"
		width="33"
		height="12"
		viewBox="0 0 33 12"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M16.5 6C16.5 2.68629 13.7298 0 10.3125 0H6.1875C2.77024 0 0 2.68629 0 6C0 9.31371 2.77024 12 6.1875 12H10.3125C13.7298 12 16.5 9.31371 16.5 6Z"
			fill="white"
			fillOpacity="0.88"
		/>
		<path
			d="M16.5 0H29.9062C31.6149 0 33 1.34315 33 3C33 4.65685 31.6149 6 29.9062 6H22.6875C19.2702 6 16.5 3.31371 16.5 0Z"
			fill="white"
			fillOpacity="0.88"
		/>
		<path
			d="M24.75 12C26.4586 12 27.8437 10.6569 27.8437 9C27.8437 7.34315 26.4586 6 24.75 6H22.6875C19.2702 6 16.5 8.68629 16.5 12H24.75Z"
			fill="white"
			fillOpacity="0.88"
		/>
	</svg>
);

const avatars = Array.from({ length: 50 }, (_, i) => ({
	id: i + 1,
	previewSrc: `/avatars/previews/avatar-${i + 1}.webp`,
	fullSrc: `/avatars/avatar-${i + 1}.jpg`,
}));

export default function AvatarsPageClient() {
	const [showTopBlur, setShowTopBlur] = useState(false);
	const [showBottomBlur, setShowBottomBlur] = useState(true);

	useEffect(() => {
		const root = document.documentElement;
		const previousOrientation = root.dataset.lenisOrientation;
		root.dataset.lenisOrientation = "vertical";

		return () => {
			if (previousOrientation) {
				root.dataset.lenisOrientation = previousOrientation;
			} else {
				delete root.dataset.lenisOrientation;
			}
		};
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			const scrollTop = window.scrollY;
			const scrollHeight = document.documentElement.scrollHeight;
			const clientHeight = window.innerHeight;
			const scrollBottom = scrollHeight - scrollTop - clientHeight;

			setShowTopBlur(scrollTop > 50);
			setShowBottomBlur(scrollBottom > 50);
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<div
			className="relative flex min-h-screen flex-col items-center gap-12 pb-10"
			style={{ background: "#0A0A0A" }}
		>
			{/* Top gradient */}
			<div
				className={`pointer-events-none fixed left-0 right-0 top-0 z-[5] h-20 transition-opacity duration-300 ${
					showTopBlur ? "opacity-100" : "opacity-0"
				}`}
				style={{
					background:
						"linear-gradient(to bottom, #0A0A0A 0%, transparent 100%)",
				}}
			/>

			{/* Bottom gradient */}
			<div
				className={`pointer-events-none fixed bottom-0 left-0 right-0 z-[5] h-20 transition-opacity duration-300 ${
					showBottomBlur ? "opacity-100" : "opacity-0"
				}`}
				style={{
					background: "linear-gradient(to top, #0A0A0A 0%, transparent 100%)",
				}}
			/>

			<div className="flex w-full flex-col items-center gap-6 pt-10">
				<OutpaceLogo />

				<div className="flex w-full flex-col gap-3 px-4">
					<header className="sticky top-4 z-10 flex w-full items-center justify-between rounded-xl px-4 py-3 md:px-5">
						<p className="text-sm font-semibold leading-5 tracking-tight text-white/[0.88]">
							Avatars
						</p>
						<p className="text-sm font-semibold leading-5 tracking-tight text-white/[0.88]">
							by{" "}
							<a
								href="https://outpacestudios.com"
								target="_blank"
								rel="noopener"
							>
								Outpace Studios
							</a>
						</p>
					</header>

					<div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
						{avatars.map((avatar, index) => (
							<AvatarCard
								key={avatar.id}
								id={avatar.id}
								index={index}
								previewSrc={avatar.previewSrc}
								fullSrc={avatar.fullSrc}
							/>
						))}
					</div>
				</div>
			</div>

			<div className="flex flex-col items-center gap-10 text-center">
				<div className="flex flex-col items-center gap-1.5 text-sm leading-5 tracking-tight">
					<p className="font-medium text-white/[0.48]">Handcrafted by</p>
					<a
						href="https://outpacestudios.com"
						target="_blank"
						className="font-semibold text-white/[0.88]"
						rel="noopener"
					>
						Outpace Studios
					</a>
				</div>
				<p className="text-xs font-medium leading-4 tracking-tight text-white/[0.48]">
					Free to use, licensed under{" "}
					<a
						href="https://creativecommons.org/licenses/by/4.0/"
						target="_blank"
						rel="noopener"
					>
						CC BY 4.0
					</a>
					.
				</p>
			</div>

			<Toast />
		</div>
	);
}
